import { useRouter } from 'next/router'
import { Log } from '@fanfanlo'

const fileLog = new Log(true, 'DeepLinkRouter_f')
fileLog.pause = true;
fileLog.childrenPaused = true;
/**
 * Deep Link 事件数据结构
 * 对应 Android 端 DeepLinkHandler.GotoPageEvent
 */
interface GotoPageEvent {
    path: string        // "page/examples/scripts"
    category?: string   // "beginner", "intermediate", "advanced"
    name?: string       // 脚本名称
    script?: boolean    // 是否需要脚本内容
}

/**
 * A11y 节点选择事件数据结构
 * 对应 Kotlin 端 A11yNodeSelectHandler.A11yNodeSelectedJsData
 */
interface A11yNodeSelectedEvent {
    projectId: string | null
    action: string | null
    pn: string | null
    an: string | null
    cn: string | null
    node: any | null
}

/**
 * 全局 router 实例
 * 在初始化时由 initDeepLinkRouter() 设置
 */
let globalRouter: ReturnType<typeof useRouter> | null = null

/**
 * 初始化 Deep Link Router
 * 必须在应用启动时调用（preferably in _app.tsx 或 _app.js）
 *
 * 使用示例：
 * ```typescript
 * // 在 pages/_app.tsx 中
 * import { initDeepLinkRouter } from '@src/libs/mc/main/DeepLinkRouter'
 *
 * function MyApp({ Component, pageProps }: AppProps) {
 *   const router = useRouter()
 *
 *   useEffect(() => {
 *     initDeepLinkRouter(router)
 *   }, [router])
 *
 *   return <Component {...pageProps} />
 * }
 * ```
 *
 * @param router - Next.js useRouter 返回值
 */
export function initDeepLinkRouter(router: ReturnType<typeof useRouter>) {
    const logData: any = {
        step: 'initDeepLinkRouter_start',
        timestamp: Date.now(),
        hasGlobalRouter: globalRouter !== null,
        routerExists: router !== undefined,
        routerType: typeof router
    };
    fileLog.log('FLOW', JSON.stringify(logData));

    if (globalRouter !== null) {
        logData.step = 'initDeepLinkRouter_alreadyInitialized';
        logData.globalRouter = 'exists';
        fileLog.log('FLOW', JSON.stringify(logData));
        return;
    }

    globalRouter = router;

    logData.step = 'initDeepLinkRouter_routerSet';
    logData.globalRouterSetSuccess = globalRouter !== null;
    fileLog.log('FLOW', JSON.stringify(logData));

    // 监听 Android 端发送的 gotoUrl 事件
    if (typeof window !== 'undefined') {
        window.addEventListener('gotoUrl', handleGotoUrlEvent as EventListener);

        logData.step = 'initDeepLinkRouter_gotoUrlListenerAdded';
        logData.eventName = 'gotoUrl';
        fileLog.log('FLOW', JSON.stringify(logData));

        // 监听 Android 端发送的 a11yNodeSelected 事件
        window.addEventListener('a11yNodeSelected', handleA11yNodeSelectedEvent as EventListener);

        logData.step = 'initDeepLinkRouter_a11yNodeSelectedListenerAdded';
        logData.eventName = 'a11yNodeSelected';
        logData.windowExists = true;
        fileLog.log('FLOW', JSON.stringify(logData));

        // 增加调试钩子,供 Android 端验证
        (window as any).__deepLinkRouterDebug = {
            initialized: true,
            hasGlobalRouter: globalRouter !== null,
            getGlobalRouter: () => globalRouter,
            checkEventListener: () => {
                return {
                    hasWindow: typeof window !== 'undefined',
                    timestamp: Date.now()
                };
            }
        };

        logData.step = 'initDeepLinkRouter_debugHookInstalled';
        fileLog.log('FLOW', JSON.stringify(logData));

    } else {
        logData.step = 'initDeepLinkRouter_noWindow';
        logData.windowExists = false;
        fileLog.error('FLOW', JSON.stringify(logData));
    }
}

/**
 * 清理 Deep Link Router
 * 可选：在应用卸载时调用以移除事件监听
 */
export function cleanupDeepLinkRouter() {
    fileLog.log('DeepLinkRouter', 'Cleaning up Deep Link Router')

    if (typeof window !== 'undefined') {
        window.removeEventListener('gotoUrl', handleGotoUrlEvent as EventListener)
        window.removeEventListener('a11yNodeSelected', handleA11yNodeSelectedEvent as EventListener)
    }

    globalRouter = null
}

/**
 * 处理 gotoUrl 事件
 * 这是一个自定义事件的处理函数，会被 window.addEventListener 调用
 *
 * @param event - CustomEvent，event.detail 包含 GotoPageEvent 数据
 */
function handleGotoUrlEvent(event: Event) {
    const customEvent = event as CustomEvent<GotoPageEvent>
    const gotoPageEvent = customEvent.detail

    fileLog.log('DeepLinkRouter', `Received gotoUrl event: ${JSON.stringify(gotoPageEvent)}`)

    if (!globalRouter) {
        fileLog.error('DeepLinkRouter', 'Router not initialized, cannot navigate')
        return
    }

    try {
        // 构建目标 URL
        const targetUrl = buildTargetUrl(gotoPageEvent)

        fileLog.log('DeepLinkRouter', `Navigating to: ${targetUrl}`)

        // 使用 router.push() 进行页面跳转
        globalRouter.push(targetUrl).then(() => {
            fileLog.log('DeepLinkRouter', 'Navigation successful')
        }).catch((error) => {
            fileLog.error('DeepLinkRouter', `Navigation failed: ${error}`)
        })
    } catch (error) {
        fileLog.error('DeepLinkRouter', `Error handling gotoUrl event: ${error}`)
    }
}

/**
 * 处理 a11yNodeSelected 事件
 * 当用户在 NodeOverlay 中选择节点后，会收到此事件
 *
 * @param event - CustomEvent，event.detail 包含 A11yNodeSelectedEvent 数据
 */
function handleA11yNodeSelectedEvent(event: Event) {
    const logData: any = {
        step: 'handleA11yNodeSelectedEvent_start',
        timestamp: Date.now(),
        eventType: event.type,
        hasGlobalRouter: globalRouter !== null,
        globalRouterType: typeof globalRouter
    };
    fileLog.log('FLOW', JSON.stringify(logData));

    if (!globalRouter) {
        logData.step = 'handleA11yNodeSelectedEvent_noRouter';
        logData.error = 'globalRouter is null or undefined';
        fileLog.error('FLOW', JSON.stringify(logData));
        return;
    }

    const customEvent = event as CustomEvent<A11yNodeSelectedEvent>;
    const nodeSelectedEvent = customEvent.detail;

    logData.step = 'handleA11yNodeSelectedEvent_dataReceived';
    logData.hasDetail = !!nodeSelectedEvent;
    logData.detailKeys = nodeSelectedEvent ? Object.keys(nodeSelectedEvent) : [];
    fileLog.log('FLOW', JSON.stringify(logData));

    try {
        const projectId = nodeSelectedEvent.projectId;
        const action = nodeSelectedEvent.action;

        logData.step = 'handleA11yNodeSelectedEvent_dataExtracted';
        logData.projectId = projectId;
        logData.action = action;
        logData.hasProjectId = !!projectId;
        logData.hasAction = !!action;
        fileLog.log('FLOW', JSON.stringify(logData));

        if (projectId === null || projectId === undefined || projectId === '') {
            logData.step = 'handleA11yNodeSelectedEvent_invalidProjectId';
            logData.error = 'projectId is null, undefined or empty';
            fileLog.error('FLOW', JSON.stringify(logData));
            return;
        }

        if (action === null || action === undefined || action === '') {
            logData.step = 'handleA11yNodeSelectedEvent_invalidAction';
            logData.error = 'action is null, undefined or empty';
            fileLog.error('FLOW', JSON.stringify(logData));
            return;
        }

        // 根据 action 决定导航路径
        if (action === 'addNodeClick') {
            const pn = nodeSelectedEvent.pn;
            const an = nodeSelectedEvent.an;
            const cn = nodeSelectedEvent.cn;
            const node = nodeSelectedEvent.node;

            logData.step = 'handleA11yNodeSelectedEvent_buildingParams';
            logData.pn = pn;
            logData.an = an;
            logData.cn = cn;
            logData.hasNode = !!node;
            fileLog.log('FLOW', JSON.stringify(logData));

            // 构建查询参数
            const params = new URLSearchParams();
            params.append('id', projectId);
            params.append('action', action);

            // 将节点数据编码为 base64
            const nodeData = {
                pn: pn,
                an: an,
                cn: cn,
                node: node
            };

            logData.step = 'handleA11yNodeSelectedEvent_encodingNodeData';
            logData.nodeDataStringLength = JSON.stringify(nodeData).length;
            fileLog.log('FLOW', JSON.stringify(logData));

            const nodeDataJson = JSON.stringify(nodeData);

            // 使用 UTF-8 safe 的 base64 编码(支持中文)
            const nodeDataBase64 = encodeUTF8ToBase64(nodeDataJson);

            logData.step = 'handleA11yNodeSelectedEvent_nodeDataEncoded';
            logData.base64Length = nodeDataBase64.length;
            fileLog.log('FLOW', JSON.stringify(logData));

            params.append('data', nodeDataBase64);

            // 构建目标 URL: /project-editor/?id=xxx&action=addNodeClick&data=xxx
            const targetUrl = `/project-editor/?${params.toString()}`;

            logData.step = 'handleA11yNodeSelectedEvent_navigating';
            logData.targetUrl = targetUrl;
            logData.urlLength = targetUrl.length;
            fileLog.log('FLOW', JSON.stringify(logData));

            // 使用 router.push() 进行页面跳转
            globalRouter.push(targetUrl).then(() => {
                logData.step = 'handleA11yNodeSelectedEvent_navigationSuccess';
                fileLog.log('FLOW', JSON.stringify(logData));
            }).catch((error) => {
                logData.step = 'handleA11yNodeSelectedEvent_navigationError';
                logData.error = String(error);
                logData.errorMessage = error?.message;
                logData.errorStack = error?.stack;
                fileLog.error('FLOW', JSON.stringify(logData));
            });
        } else {
            logData.step = 'handleA11yNodeSelectedEvent_unsupportedAction';
            logData.action = action;
            fileLog.log('FLOW', JSON.stringify(logData));
        }
    } catch (error) {
        logData.step = 'handleA11yNodeSelectedEvent_exception';
        logData.error = String(error);
        logData.errorMessage = (error as any)?.message;
        logData.errorStack = (error as any)?.stack;
        fileLog.error('FLOW', JSON.stringify(logData));
    }
}

/**
 * 根据 GotoPageEvent 构建目标 URL
 *
 * 示例：
 * - GotoPageEvent { path: "page/examples/scripts", category: "beginner", name: "vibrate" }
 * - 构建为: /examples/scripts/beginner/vibrate
 *
 * @param event - Deep Link 事件数据
 * @returns 目标 URL
 */
function buildTargetUrl(event: GotoPageEvent): string {
    // 基础路径处理
    let url = event.path

    fileLog.log('DeepLinkRouter', `buildTargetUrl: initial path = ${url}`)

    // 如果路径包含 "page/"，移除它
    if (url.startsWith('page/')) {
        url = url.replace('page/', '')
        fileLog.log('DeepLinkRouter', `buildTargetUrl: removed 'page/' prefix, url = ${url}`)
    }

    // 添加斜杠前缀
    if (!url.startsWith('/')) {
        url = '/' + url
        fileLog.log('DeepLinkRouter', `buildTargetUrl: added leading slash, url = ${url}`)
    }

    // ✅ 构建查询参数（修改：不再添加到路径中）
    const params = new URLSearchParams()

    if (event.category) {
        params.append('category', event.category)
        fileLog.log('DeepLinkRouter', `buildTargetUrl: added category param = ${event.category}`)
    }

    if (event.name) {
        params.append('name', event.name)
        fileLog.log('DeepLinkRouter', `buildTargetUrl: added name param = ${event.name}`)
    }

    if (event.script !== undefined) {
        params.append('script', String(event.script))
        fileLog.log('DeepLinkRouter', `buildTargetUrl: added script param = ${event.script}`)
    }

    // 附加所有查询参数到 URL
    if (params.toString()) {
        url = `${url}?${params.toString()}`
        fileLog.log('DeepLinkRouter', `buildTargetUrl: appended query params, url = ${url}`)
    }

    fileLog.log('DeepLinkRouter', `buildTargetUrl: final url = ${url}`)

    return url
}

/**
 * 手动导航到脚本页面
 * 可以在其他地方调用这个函数来实现 Deep Link 导航
 *
 * 使用示例：
 * ```typescript
 * navigateToScript('beginner', 'vibrate', true)
 * // 导航到: /examples/scripts/beginner/vibrate?script=true
 * ```
 *
 * @param category - 脚本难度级别
 * @param name - 脚本名称
 * @param script - 是否需要脚本内容
 */
export function navigateToScript(category: string, name: string, script: boolean = true) {
    const event: GotoPageEvent = {
        path: 'examples/scripts',
        category,
        name,
        script,
    }

    fileLog.log('DeepLinkRouter', `navigateToScript called: ${JSON.stringify(event)}`)

    handleGotoUrlEvent(new CustomEvent('gotoUrl', { detail: event }))
}

/**
 * 将字符串编码为 UTF-8 安全的 Base64
 * 支持中文和其他非 Latin1 字符
 *
 * @param str - 要编码的字符串
 * @returns Base64 编码后的字符串
 */
function encodeUTF8ToBase64(str: string): string {
    fileLog.log('DeepLinkRouter', `encodeUTF8ToBase64 called with str length = ${str.length}`)

    try {
        // 步骤 1: 使用 TextEncoder 将字符串转换为 UTF-8 字节数组
        const encoder = new TextEncoder();
        const utf8Bytes = encoder.encode(str);
        fileLog.log('DeepLinkRouter', `utf8Bytes length = ${utf8Bytes.length}`)

        // 步骤 2: 将字节数组转换为二进制字符串
        let binaryString = '';
        for (let i = 0; i < utf8Bytes.length; i++) {
            binaryString += String.fromCharCode(utf8Bytes[i]);
            fileLog.log('DeepLinkRouter', `converted byte ${i} to binary char`)
        }
        fileLog.log('DeepLinkRouter', `binaryString length = ${binaryString.length}`)

        // 步骤 3: 使用 btoa 将二进制字符串编码为 Base64
        const base64 = btoa(binaryString);
        fileLog.log('DeepLinkRouter', `base64 length = ${base64.length}`)

        return base64;
    } catch (error) {
        fileLog.error('DeepLinkRouter', `encodeUTF8ToBase64 error: ${error}`)
        throw error;
    }
}

